import { create } from 'zustand';
import type { Industry, MerchantProfile, DiagnosisModule } from '@/types';
import type { MerchantFormData } from '@/pages/onboarding/MerchantProfileForm';

/** Customer profile form data (Step 3) */
export interface CustomerProfileData {
  core_customer_segment: string;
  target_age_range: string;
  target_gender_distribution: { male: number; female: number };
  target_location_type: string;
  target_consumption_level: string;
  target_interests: string[];
  target_pain_points: string;
  target_buying_frequency: string;
  repurchase_rate: number; // percentage 0-100
  // Enhanced fields
  customer_layers?: { core: string; potential: string; low_efficiency: string };
  avg_customer_value?: string;
  top_consultation_questions?: string;
  customer_lifecycle_sop?: { acquisition: string; conversion: string; retention: string };
}

/** IP persona data (Step 4) */
export interface PersonaData {
  appearance: string;
  style: string[];
  expertise: string[];
  differentiation: string[];
  content_focus?: string;
  tone?: string;
  // Enhanced fields
  persona_positioning?: string;
  personal_traits?: string[];
  memorable_elements?: string;
  trust_building?: string[];
  visual_style?: string;
  content_direction?: string[];
  role_models?: string;
}

interface OnboardingState {
  step: 1 | 2 | 3 | 4 | 5;
  industry: Industry | null;
  profile: Partial<MerchantProfile>;
  formData: MerchantFormData | null;
  cpData: CustomerProfileData | null;
  personaData: PersonaData | null;
  chatRound: number;
  selectedModules: DiagnosisModule[];
  setStep: (step: 1 | 2 | 3 | 4 | 5) => void;
  setIndustry: (industry: Industry) => void;
  updateProfile: (data: Partial<MerchantProfile>) => void;
  setFormData: (data: MerchantFormData) => void;
  setCpData: (data: CustomerProfileData) => void;
  setPersonaData: (data: PersonaData) => void;
  incrementChatRound: () => void;
  toggleModule: (code: DiagnosisModule) => void;
  reset: () => void;
}

const initialState = {
  step: 1 as const,
  industry: null as Industry | null,
  profile: {} as Partial<MerchantProfile>,
  formData: null as MerchantFormData | null,
  cpData: null as CustomerProfileData | null,
  personaData: null as PersonaData | null,
  chatRound: 0,
  selectedModules: [] as DiagnosisModule[],
};

export const useOnboardingStore = create<OnboardingState>((set) => ({
  ...initialState,

  setStep: (step) => set({ step }),
  setIndustry: (industry) => set({ industry }),
  updateProfile: (data) => set((state) => ({ profile: { ...state.profile, ...data } })),
  setFormData: (data) => set({ formData: data }),
  setCpData: (data) => set({ cpData: data }),
  setPersonaData: (data) => set({ personaData: data }),
  incrementChatRound: () => set((state) => ({ chatRound: state.chatRound + 1 })),
  toggleModule: (code) => set((state) => ({
    selectedModules: state.selectedModules.includes(code)
      ? state.selectedModules.filter((m) => m !== code)
      : [...state.selectedModules, code],
  })),
  reset: () => set(initialState),
}));
